import { renderNav } from '../components/nav.js';
import { FlockWS, type BroadcastRecord, type WSState } from '../ws-client.js';
import { flockServerHttpUrl, flockServerWsUrl } from '../config.js';
import { matchRule } from '../raven-core/index.js';
import { getKeyForFrequency, type LoadedKey } from '../key-handoff.js';

const RAVEN_WEBAPP_BASE = 'https://sendraven.ink';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function ts(ms: number): string {
  return new Date(ms).toISOString().slice(11, 19);
}

interface FrequencyPageState {
  frequency: string;
  key: LoadedKey | undefined;
  rendered: BroadcastRecord[];
}

function rowHtml(b: BroadcastRecord, key: LoadedKey | undefined): string {
  const matched = key !== undefined && matchRule(b.carrier_text, key.encodingRule);
  const klass = matched ? 'broadcast-row match' : 'broadcast-row';
  const text = escapeHtml(b.carrier_text);
  const ravenLink = matched
    ? `<a class="raven-detected" href="${ravenScanUrl(b)}" target="_blank" rel="noopener">raven detected — open in Raven</a>`
    : '';
  return `
    <div class="${klass}" data-id="${b.id}">
      <div class="freq">${escapeHtml(b.frequency)}</div>
      <div class="text">${text}${ravenLink === '' ? '' : `<div class="raven-line">${ravenLink}</div>`}</div>
      <div class="ts">${ts(b.stored_at)}</div>
    </div>
  `;
}

function ravenScanUrl(b: BroadcastRecord): string {
  const params = new URLSearchParams({
    frequency: b.frequency,
    carrier: b.carrier_text,
  });
  return `${RAVEN_WEBAPP_BASE}/scan#${params.toString()}`;
}

function ravenComposeUrl(text: string, frequency: string): string {
  const params = new URLSearchParams({
    carrier: text,
    frequency,
    dest: 'flock',
  });
  return `${RAVEN_WEBAPP_BASE}/compose#${params.toString()}`;
}

export function mountFrequency(root: HTMLElement, frequency: string): () => void {
  const key = getKeyForFrequency(frequency);
  const state: FrequencyPageState = {
    frequency,
    key,
    rendered: [],
  };

  root.innerHTML = `
    ${renderNav({ active: 'frequencies' })}
    <section class="freq-detail">
      <header class="freq-detail-header">
        <a class="back-link" href="#/frequencies">← all frequencies</a>
        <div class="freq-detail-addr">${escapeHtml(frequency)}</div>
        <div class="freq-detail-label">${key === undefined ? '<span class="key-missing">no key loaded</span>' : escapeHtml(key.label)}</div>
      </header>
      <main class="firehose-stream" data-stream>
        <div class="firehose-empty" data-empty>connecting…</div>
      </main>
      <form class="compose" data-compose>
        <input type="text" name="carrier" placeholder="broadcast on this frequency…" maxlength="500" autocomplete="off" />
        <button type="submit" class="btn btn-primary" data-action="broadcast">broadcast</button>
        ${key === undefined ? '' : '<a class="btn btn-outline" data-action="send-as-raven">send as raven</a>'}
        <span class="compose-error" data-error></span>
      </form>
    </section>
  `;

  const stream = root.querySelector<HTMLElement>('[data-stream]');
  const empty = root.querySelector<HTMLElement>('[data-empty]');
  const form = root.querySelector<HTMLFormElement>('[data-compose]');
  const carrierInput = form?.querySelector<HTMLInputElement>('input[name="carrier"]') ?? null;
  const error = form?.querySelector<HTMLElement>('[data-error]') ?? null;
  const sendAsRaven = form?.querySelector<HTMLAnchorElement>('[data-action="send-as-raven"]') ?? null;

  if (stream === null || form === null || carrierInput === null) {
    return () => undefined;
  }

  const renderRow = (b: BroadcastRecord): void => {
    if (empty !== null && empty.parentElement === stream) empty.remove();
    state.rendered.push(b);
    stream.insertAdjacentHTML('beforeend', rowHtml(b, state.key));
    while (state.rendered.length > 200) {
      state.rendered.shift();
      const first = stream.firstElementChild;
      if (first !== null) first.remove();
    }
    stream.scrollTop = stream.scrollHeight;
  };

  const setStateLabel = (s: WSState): void => {
    if (empty === null) return;
    if (s === 'connecting') empty.textContent = 'connecting…';
    else if (s === 'closed') empty.textContent = 'reconnecting…';
    else if (s === 'open' && state.rendered.length === 0) empty.textContent = 'connected. waiting…';
  };

  const ws = new FlockWS({
    url: `${flockServerWsUrl()}/v1/tune/${encodeURIComponent(frequency)}`,
    onState: setStateLabel,
    onHistory: (broadcasts) => {
      for (const b of broadcasts) renderRow(b);
    },
    onBroadcast: (b) => renderRow(b),
  });
  ws.connect();

  // Compose actions
  const updateRavenLink = (): void => {
    if (sendAsRaven === null) return;
    sendAsRaven.href = ravenComposeUrl(carrierInput.value, frequency);
  };
  carrierInput.addEventListener('input', updateRavenLink);
  updateRavenLink();

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    void postBroadcast();
  });

  async function postBroadcast(): Promise<void> {
    const text = carrierInput?.value.trim() ?? '';
    if (carrierInput === null || error === null) return;
    if (text === '') return;
    error.textContent = '';
    try {
      const res = await fetch(`${flockServerHttpUrl()}/v1/broadcast`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          version: 1,
          frequency,
          carrier_text: text,
          timestamp: Date.now(),
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        error.textContent = `rejected: ${body.error ?? res.status}`;
        return;
      }
      carrierInput.value = '';
      updateRavenLink();
    } catch (err) {
      error.textContent = `network: ${(err as Error).message}`;
    }
  }

  return () => ws.disconnect();
}
